import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import logUpsellPlanCompareTapped from '@alltrails/amplitude/events/logUpsellPlanCompareTapped';
import logUpsellProductClicked from '@alltrails/amplitude/events/logUpsellProductClicked';
import useCurrencyCode from '@alltrails/context/hooks/useCurrencyCode';
import Typography from '@alltrails/core/components/Typography';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import Link from '@alltrails/denali/components/Link';
import PeakLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Peak';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import TrialReminder from '@alltrails/payment/components/CheckoutBase/TrialReminder';
import getPurchaseScreenTypePlan from '@alltrails/payment/utils/getPurchaseScreenTypePlan';
import type Coupon from '@alltrails/types/Coupon';
import type Plans from '@alltrails/types/Plans';
import type SubscriptionUser from '@alltrails/types/SubscriptionUser';
import getPricingText from './getPricingText';
import styles from './PlanSelection.module.scss';

// Figma file: https://www.figma.com/design/PtYSSpohQ9rL7ZWCcdsYJK/2025-Upsell-System?node-id=5541-138083&t=JJQ3VDYh6Mc0P7Ol-0

type Props = {
  className?: string;
  hideCompareLink?: boolean;
  hidePeak?: boolean;
  plans: Plans;
  coupon?: Coupon;
  setTierSelected: (tier: PurchaseTier) => void;
  showPlusFirst?: boolean;
  tierSelected: PurchaseTier;
  trigger: UpsellTrigger;
  isPromoEligible: boolean;
  subscriptionUser: SubscriptionUser | null;
};

const PlanSelection = ({
  className,
  hideCompareLink = false,
  hidePeak = false,
  plans,
  coupon,
  setTierSelected,
  showPlusFirst = false,
  tierSelected,
  trigger,
  isPromoEligible,
  subscriptionUser
}: Props) => {
  const currencyCode = useCurrencyCode();
  const isMobileSizedScreen = useIsMobileSizedScreen();

  const scrollToFeatureList = () => {
    const featureListElement = document.getElementById('feature-list');
    if (featureListElement) {
      featureListElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const plusPlanButton = (
    <button
      className={classNames(styles.planContainer, {
        [styles.plusSelected]: tierSelected === 'plus'
      })}
      onClick={() => {
        logUpsellProductClicked({
          product_type: 'plus',
          purchase_screen_type_plan: getPurchaseScreenTypePlan(plans.plus, coupon),
          trigger
        });

        setTierSelected('plus');
      }}
    >
      <div className={styles.planContentContainer}>
        <PlusLogoBadge variant="full" />
        <Typography variant="text200" className={styles.description}>
          <FormattedMessage defaultMessage="Offline maps, trail previews, and more" />
        </Typography>
        <Typography variant="text100">
          {getPricingText({
            plan: plans.plus,
            currencyCode,
            isPromoEligible,
            coupon: coupon?.subscriptionTier === 'plus' || coupon?.subscriptionTier === 'any' ? coupon : undefined,
            isMobileSizedScreen,
            subscriptionUser
          })}
        </Typography>
        {plans.plus.isTrial && (
          <TrialReminder selected={tierSelected === 'plus'} tier="plus" currencyCode={currencyCode} cost={plans.plus.priceTotal} variant="minimal" />
        )}
      </div>
    </button>
  );

  return (
    <div className={classNames(styles.planSelectionWrapper, className)}>
      {!hideCompareLink && (
        <div className={styles.topRow}>
          <Typography variant="text100bold">
            <FormattedMessage defaultMessage="Choose your plan" />
          </Typography>
          <Link
            size="sm"
            variant="primary"
            onClick={() => {
              logUpsellPlanCompareTapped({
                purchase_screen_type_plan: getPurchaseScreenTypePlan(plans.peak, coupon),
                trigger
              });
              scrollToFeatureList();
            }}
          >
            <FormattedMessage defaultMessage="Compare" />
          </Link>
        </div>
      )}
      {showPlusFirst && plusPlanButton}
      {!hidePeak && (
        <button
          className={classNames(styles.planContainer, {
            [styles.peakSelected]: tierSelected === 'peak'
          })}
          onClick={() => {
            logUpsellProductClicked({
              product_type: 'peak',
              purchase_screen_type_plan: getPurchaseScreenTypePlan(plans.peak, coupon),
              trigger
            });

            setTierSelected('peak');
          }}
        >
          <div className={styles.planContentContainer}>
            <div className={styles.peakRow}>
              <PeakLogoBadge variant="full" />
              <Typography variant="text50bold" className={styles.badge}>
                <FormattedMessage defaultMessage="Includes Plus benefits" />
              </Typography>
            </div>
            <Typography variant="text200" className={styles.description}>
              Custom routes, advanced map layers and more
            </Typography>
            <Typography variant="text100">
              {getPricingText({
                plan: plans.peak,
                currencyCode,
                isPromoEligible,
                coupon: coupon?.subscriptionTier === 'peak' || coupon?.subscriptionTier === 'any' ? coupon : undefined,
                isMobileSizedScreen,
                subscriptionUser
              })}
            </Typography>
            {plans.peak.isTrial && (
              <TrialReminder
                selected={tierSelected === 'peak'}
                tier="peak"
                currencyCode={currencyCode}
                cost={plans.peak.priceTotal}
                variant="minimal"
              />
            )}
          </div>
        </button>
      )}
      {!showPlusFirst && plusPlanButton}
    </div>
  );
};
export default PlanSelection;
